import React from "react";
import { Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { useLoginModal } from "../../context/LoginModalContext";
import SheetGround from "../../components/shared/ui/SheetGround";
import { VELLUM as T, SANS, display, mono, body, btn, SheetHead } from "../../components/shared/folio/kit";

/* Who keeps the register, drawn as a folio.

   Each part of the page is a sheet section: a heading with its code on the
   same baseline, opened by a heavy rule and ruled into columns underneath.
   The old page banded alternate sections in a second paper colour; the sheet
   has one ground throughout and separates by rule instead, which is how
   every other surface on the site now reads. */

const P = "ab";

const VALUES = [
  {
    name: "Rigour",
    body:
      "Every listing is read by a working academic before it goes up. No reheated calls, no scraped duplicates, no recruiter spam.",
  },
  {
    name: "Plainness",
    body:
      "No tiers, no boosted placement, no dark patterns. Flat fees, transparent reach, and the same listing for everyone.",
  },
  {
    name: "Independence",
    body:
      "We aren't backed by a publisher or recruitment chain. Our only commitment is to the people who read the register.",
  },
];

const TEAM = [
  {
    name: "Dr. Sarah Johnson",
    role: "Founder & Editor",
    blurb:
      "Former Dean of Engineering, 15+ years in UK higher education. Reads every listing in Sciences.",
  },
  {
    name: "Prof. Michael Chen",
    role: "Head of Platform",
    blurb:
      "Computer Science professor; led research on academic labour markets at Manchester. Builds the index tooling.",
  },
  {
    name: "Dr. Emily Rodriguez",
    role: "Head of Institutional Desk",
    blurb:
      "Twelve years recruiting for research-intensive universities. Looks after the employer side of the register.",
  },
];

const FINE_PRINT = [
  "Scholars: full register, digest and applications — free.",
  "Institutions: £89 flat per listing, no surcharges.",
  "We don't share data with recruiters or publishers.",
  "Every listing is read by an editor before it goes live.",
];

// Impact numbers commented out until we have real figures to publish.
// const IMPACT: [string, string][] = [
//   ["10,000+", "Scholars registered"],
//   ["500+",    "Partner institutions"],
//   ["5,000+",  "Roles published"],
//   ["50+",     "Countries reached"],
// ];

/* Section hatching, as a drawing marks an area it has no detail for yet. */
const HATCH = `repeating-linear-gradient(135deg, rgba(21,32,29,0.07) 0 2px, transparent 2px 14px)`;

const wrap: React.CSSProperties = { maxWidth: 1280, margin: "0 auto", padding: "0 20px" };

const AboutPage: React.FC = () => {
  const { openRegisterModal } = useLoginModal();

  return (
    <SheetGround className={`${P}-root`} style={{ fontFamily: SANS, color: T.ink }}>
      {/* The sheet head */}
      <section style={{ ...wrap, paddingTop: 44 }}>
        <div
          style={{
            ...mono(T),
            borderBottom: `1px solid ${T.rule}`,
            paddingBottom: 12,
            marginBottom: 30,
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <span>§ About</span>
          <span>Founded · 2024</span>
          <span>London / Cambridge</span>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <h1 style={{ ...display(T, "clamp(36px, 5.6vw, 66px)"), lineHeight: 0.98, letterSpacing: "-0.04em" }}>
            A register, kept by{" "}
            <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>working scholars</span>.
          </h1>
          <span style={mono(T, T.accent)}>§ Who we are</span>
        </div>

        <p style={{ ...body(T), fontSize: 18, marginTop: 26, maxWidth: 680 }}>
          Connecting academic talent with world-class institutions. We are revolutionising how universities
          find the right people and how researchers discover their next post.
        </p>
      </section>

      {/* Mission / Vision - the two tracks, divided by the rule */}
      <section style={{ ...wrap, paddingTop: 56, paddingBottom: 56 }}>
        <div className={`${P}-split`} style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr", columnGap: 56 }}>
          <div className={`${P}-split-a`}>
            <SheetHead t={T} code="§ 01 · Mission" size="clamp(26px, 3.4vw, 34px)">
              What we are here to do.
            </SheetHead>
            <p style={{ ...body(T), fontSize: 17, maxWidth: 520 }}>
              To bridge the gap between academic institutions and talented professionals, creating
              meaningful connections that advance research, education and innovation worldwide.
            </p>
          </div>
          <div aria-hidden className={`${P}-split-rule`} style={{ background: T.rule, width: 1 }} />
          <div className={`${P}-split-b`}>
            <SheetHead t={T} code="§ 02 · Vision" size="clamp(26px, 3.4vw, 34px)">
              Where we are headed.
            </SheetHead>
            <p style={{ ...body(T), fontSize: 17, maxWidth: 520 }}>
              A world where every academic professional finds the right role, and every institution
              discovers the talent it needs to achieve its goals.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ ...wrap, paddingBottom: 56 }}>
        <SheetHead t={T} code="§ 03 · Values">
          What the register is for.
        </SheetHead>
        <div className={`${P}-cols`} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
          {VALUES.map((v, i) => (
            <div
              key={v.name}
              className={`${P}-cell`}
              style={{
                padding: "28px 26px",
                borderLeft: i === 0 ? "none" : `1px solid ${T.rule}`,
                borderBottom: `1px solid ${T.rule}`,
              }}
            >
              <div style={mono(T, T.accent)}>{String(i + 1).padStart(2, "0")}</div>
              <h3 style={{ ...display(T, "26px"), margin: "12px 0 14px" }}>{v.name}</h3>
              <p style={{ ...body(T), fontSize: 15.5 }}>{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section style={{ ...wrap, paddingBottom: 56 }}>
        <SheetHead t={T} code="§ 04 · Team">
          Who reads the register.
        </SheetHead>
        <div className={`${P}-cols`} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
          {TEAM.map((t, i) => (
            <div
              key={t.name}
              className={`${P}-cell`}
              style={{
                padding: "28px 26px",
                borderLeft: i === 0 ? "none" : `1px solid ${T.rule}`,
                borderBottom: `1px solid ${T.rule}`,
              }}
            >
              <div
                style={{
                  width: "100%",
                  aspectRatio: "4 / 5",
                  background: T.panel,
                  backgroundImage: HATCH,
                  border: `1px solid ${T.rule}`,
                  display: "flex",
                  alignItems: "flex-end",
                  padding: 14,
                  marginBottom: 18,
                  ...mono(T),
                  letterSpacing: "0.06em",
                }}
              >
                — Portrait
              </div>
              <h3 style={{ ...display(T, "21px"), marginBottom: 6 }}>{t.name}</h3>
              <div style={{ ...mono(T, T.accent), marginBottom: 12 }}>{t.role}</div>
              <p style={{ ...body(T), fontSize: 14.5 }}>{t.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Impact band — commented out pre-launch; restore once we have real figures. */}
      {/*
      <section style={{ ...wrap, paddingBottom: 56 }}>
        <SheetHead t={T} code="§ 05 · Impact">
          The register, in numbers.
        </SheetHead>
        <div className={`${P}-impact`} style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
          {IMPACT.map(([n, l]) => (
            <div key={n}>
              <div style={display(T, "44px")}>{n}</div>
              <div style={{ ...mono(T), marginTop: 10 }}>{l}</div>
            </div>
          ))}
        </div>
      </section>
      */}

      {/* Close */}
      <section style={{ ...wrap, paddingBottom: 72 }}>
        <div style={{ borderTop: `3px solid ${T.ink}`, paddingTop: 42 }} />
        <div className={`${P}-close`} style={{ display: "grid", gridTemplateColumns: "1.4fr 1px 1fr", columnGap: 56, alignItems: "start" }}>
          <div>
            <div style={mono(T, T.accent)}>§ Join the register</div>
            <h2 style={{ ...display(T, "clamp(32px, 5vw, 58px)"), margin: "12px 0 22px", lineHeight: 1, letterSpacing: "-0.035em" }}>
              Ready to join the community?
            </h2>
            <p style={{ ...body(T), fontSize: 18, maxWidth: 560 }}>
              Whether you're looking for your next academic role or seeking to hire top talent, we
              are here to help you succeed.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
              <button onClick={openRegisterModal} className={`${P}-accent`} style={btn(T, "accent")}>
                Find your next role
              </button>
              <Link to={ROUTE_NAMES.PAGES.POST_JOB} className={`${P}-out`} style={{ ...btn(T, "outline"), textDecoration: "none" }}>
                Post a position
              </Link>
            </div>
          </div>

          <div aria-hidden className={`${P}-close-rule`} style={{ background: T.rule, width: 1, alignSelf: "stretch" }} />

          <div className={`${P}-fine`}>
            <div style={{ ...mono(T), paddingBottom: 11, borderBottom: `1.5px solid ${T.ink}` }}>§ The fine print</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {FINE_PRINT.map((t) => (
                <li
                  key={t}
                  style={{
                    position: "relative",
                    borderBottom: `1px solid ${T.rule}`,
                    padding: "13px 0 13px 24px",
                    fontFamily: SANS,
                    fontSize: 14.5,
                    lineHeight: 1.5,
                    color: T.ink,
                  }}
                >
                  {/* Drawn, not typed: a glyph standing in for a mark is a costume. */}
                  <span
                    aria-hidden
                    style={{ position: "absolute", left: 0, top: 22, width: 10, height: 1, background: T.accent }}
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <style>{`
        .${P}-accent { transition: background 150ms ease; }
        .${P}-accent:hover { background: ${T.accentDeep}; }
        .${P}-out { transition: background 150ms ease, color 150ms ease; }
        .${P}-out:hover { background: ${T.ink}; color: ${T.ground}; }
        .${P}-accent:focus-visible, .${P}-out:focus-visible {
          outline: 2px solid ${T.accent}; outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .${P}-accent, .${P}-out { transition: none; }
        }

        @media (max-width: 900px) {
          .${P}-split, .${P}-close { grid-template-columns: 1fr !important; column-gap: 0 !important; }
          .${P}-split-rule, .${P}-close-rule { width: auto !important; height: 1px; margin: 40px 0; }
          .${P}-cols { grid-template-columns: 1fr !important; }
          .${P}-cell { border-left: none !important; }
          .${P}-fine { margin-top: 8px; }
          .${P}-impact { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </SheetGround>
  );
};

export default AboutPage;
